/**
 * iter163 — Phase 2: Grouped permission checkboxes for the Roles & Permissions page.
 *
 * Pure presentation. Receives the full perm catalog + currently-checked set;
 * emits a new array via `onChange` when the user toggles, select-alls, or clears.
 *
 * Features (per user spec):
 *  - Permission search across all groups
 *  - Collapsible groups (groups containing sensitive perms default collapsed)
 *  - Per-group count badge: "Group · 3 of 12"
 *  - Per-group Select all / Clear all
 *  - 🔒 lock icon next to sensitive perms
 *  - 🛑 red warning badge on destructive perms
 *  - Optional inline help text per perm
 *  - Optional sensitive-group banner
 *
 * Backward-compat: pure UI swap. Backend payload (`permissions: string[]`)
 * is unchanged.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Lock, AlertTriangle, Search } from 'lucide-react';
import { partitionPerms } from '../lib/permGroups';

function humanize(k) {
  return k.replace(/_/g, ' ');
}

const GroupedPermissions = ({ catalogPermissions, selected, onChange }) => {
  const groups = useMemo(() => partitionPerms(catalogPermissions || []), [catalogPermissions]);
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState(() => {
    // Default: collapse groups that have any sensitive perm; expand the rest.
    const initial = {};
    for (const g of groups) {
      const hasSensitive = g.perms.some((p) => p.sensitive);
      initial[g.key] = !hasSensitive;
    }
    return initial;
  });

  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  const q = query.trim().toLowerCase();
  const matches = (p) => !q || p.key.toLowerCase().includes(q) || humanize(p.key).includes(q);
  // When a search is active, force-open every group so the user sees hits.
  const isOpen = (g) => (q ? true : openGroups[g.key]);

  const toggleGroup = (gKey) => setOpenGroups((s) => ({ ...s, [gKey]: !s[gKey] }));

  const togglePerm = (permKey) => {
    if (selectedSet.has(permKey)) {
      onChange(selected.filter((p) => p !== permKey));
    } else {
      onChange([...selected, permKey]);
    }
  };

  const selectAllInGroup = (g) => {
    const keys = g.perms.filter(matches).map((p) => p.key);
    const merged = Array.from(new Set([...selected, ...keys]));
    onChange(merged);
  };

  const clearAllInGroup = (g) => {
    const keys = new Set(g.perms.filter(matches).map((p) => p.key));
    onChange(selected.filter((p) => !keys.has(p)));
  };

  const totalCatalog = (catalogPermissions || []).length;
  const totalSelected = (selected || []).length;

  return (
    <div data-testid="grouped-perms" className="space-y-3">
      {/* Top bar: search + total counter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#525252] absolute left-2 top-2.5" />
          <input
            data-testid="perm-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search permissions (e.g. invoice, pay, archive)…"
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded pl-8 pr-2 py-1.5 text-sm text-white placeholder-[#525252] focus:outline-none focus:border-[#D4AF37]"
          />
        </div>
        <span data-testid="perm-total-count" className="text-xs text-[#A3A3A3] whitespace-nowrap">
          <span className="text-[#D4AF37] font-bold">{totalSelected}</span> of {totalCatalog} granted
        </span>
      </div>

      {groups.map((g) => {
        const visiblePerms = g.perms.filter(matches);
        if (visiblePerms.length === 0) return null;
        const groupGranted = visiblePerms.filter((p) => selectedSet.has(p.key)).length;
        const open = isOpen(g);

        return (
          <div
            key={g.key}
            data-testid={`perm-group-${g.key}`}
            className="border border-[#262626] rounded-md bg-[#0A0A0A]/40"
          >
            {/* Group header */}
            <div className="flex items-center justify-between px-3 py-2 gap-2">
              <button
                data-testid={`perm-group-toggle-${g.key}`}
                onClick={() => toggleGroup(g.key)}
                className="flex items-center gap-2 text-left flex-1 min-w-0"
              >
                {open ? <ChevronDown className="w-4 h-4 text-[#A3A3A3]" /> : <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />}
                <span className="text-white font-bold text-sm truncate">{g.label}</span>
                <span className="text-[10px] text-[#A3A3A3] whitespace-nowrap">
                  · <span className="text-[#D4AF37] font-bold">{groupGranted}</span> of {visiblePerms.length}
                </span>
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={`perm-group-all-${g.key}`}
                  onClick={() => selectAllInGroup(g)}
                  className="text-[10px] text-[#A3A3A3] hover:text-[#D4AF37] px-1.5 py-0.5"
                >
                  Select all
                </button>
                <span className="text-[#262626]">|</span>
                <button
                  data-testid={`perm-group-clear-${g.key}`}
                  onClick={() => clearAllInGroup(g)}
                  className="text-[10px] text-[#A3A3A3] hover:text-red-400 px-1.5 py-0.5"
                >
                  Clear all
                </button>
              </div>
            </div>

            {open && (
              <div className="px-3 pb-3 space-y-2">
                {g.description && (
                  <p className="text-[11px] text-[#A3A3A3]">{g.description}</p>
                )}
                {g.warning && (
                  <div
                    data-testid={`perm-group-warning-${g.key}`}
                    className="flex items-start gap-2 bg-[#2A1810] border border-[#7F1D1D]/50 rounded px-2 py-1.5"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-red-300 leading-tight">{g.warning}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {visiblePerms.map((p) => {
                    const checked = selectedSet.has(p.key);
                    return (
                      <label
                        key={p.key}
                        className={`flex items-start gap-2 bg-[#0A0A0A] border rounded px-2.5 py-1.5 cursor-pointer transition-colors ${
                          checked ? 'border-[#D4AF37]/60' : 'border-[#262626] hover:border-[#525252]'
                        }`}
                      >
                        <input
                          data-testid={`perm-${p.key}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          className="accent-[#D4AF37] mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-white text-sm">{humanize(p.key)}</span>
                            {p.sensitive && (
                              <span title="Sensitive permission" className="inline-flex items-center text-amber-400">
                                <Lock className="w-3 h-3" />
                              </span>
                            )}
                            {p.danger && (
                              <span
                                data-testid={`perm-danger-${p.key}`}
                                className="text-[9px] uppercase tracking-wider bg-red-900/40 text-red-300 border border-red-800/60 rounded px-1 py-px"
                              >
                                Destructive
                              </span>
                            )}
                          </div>
                          {p.help && (
                            <p className="text-[10px] text-[#A3A3A3] mt-0.5 leading-snug">{p.help}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GroupedPermissions;
